/**
 * Orchestrates YOLO ball detection, hand tracking, Kalman tracking,
 * event detection, and recording for the JuggleVision v1 spec.
 */
import { YoloBallDetector } from './YoloBallDetector';
import { BallTracker } from './BallTracker';
import { HandTracker } from './HandTracker';
import { EventDetector } from './EventDetector';
import type { BallTrack } from './BallTracker';
import type { HandPosition } from './HandTracker';
export interface PipelineResult {
  tracks: BallTrack[];
  hands: HandPosition[];
  throwCount: number;
  catchCount: number;
  totalThrows: number;
  totalCatches: number;
  leftThrows: number;
  rightThrows: number;
  leftPct: number;
  rightPct: number;
  meanPeakHeight: number;
  isCalibrating: boolean;
  error: string | null;
}

export class JuggleVisionPipeline {
  private yolo = new YoloBallDetector();
  private tracker = new BallTracker();
  private hands = new HandTracker();
  private events = new EventDetector();
  private handFrameCounter = 0;
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private lastHands: HandPosition[] = [];

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      await Promise.all([this.yolo.initialize(), this.hands.initialize()]);
      this.initialized = true;
    })();

    return this.initPromise;
  }

  isReady(): boolean {
    return this.yolo.isReady();
  }

  getError(): string | null {
    if (this.initialized && !this.yolo.isReady()) {
      return 'YOLO model failed to load. Add yolo-juggle.onnx to public/models/.';
    }
    return null;
  }

  async processFrame(
    video: HTMLVideoElement,
    timestampMs: number,
    recorder: {
      recordFrame: (
        ts: number,
        balls: Array<{ id: number; x: number; y: number; vx?: number; vy?: number }>,
        hands?: Array<{ isLeft: boolean; x: number; y: number }>
      ) => void;
    }
  ): Promise<PipelineResult> {
    const defaultResult: PipelineResult = {
      tracks: [],
      hands: [],
      throwCount: 0,
      catchCount: 0,
      totalThrows: 0,
      totalCatches: 0,
      leftThrows: 0,
      rightThrows: 0,
      leftPct: 50,
      rightPct: 50,
      meanPeakHeight: 0,
      isCalibrating: false,
      error: null,
    };

    if (!this.yolo.isReady()) {
      defaultResult.error = this.getError();
      return defaultResult;
    }

    try {
      const detections = await this.yolo.detect(video);
      const tracks = this.tracker.update(detections);

      this.events.setFrameHeight(video.videoHeight);

      this.handFrameCounter++;
      if (this.handFrameCounter % 3 === 0 && this.hands.isReady()) {
        this.lastHands = this.hands.detect(video, timestampMs);
      }

      const { throwCount, catchCount } = this.events.update(
        tracks,
        this.lastHands,
        timestampMs
      );

      recorder.recordFrame(
        timestampMs,
        tracks.map((t) => ({ id: t.id, x: t.x, y: t.y, vx: t.vx, vy: t.vy })),
        this.lastHands
      );

      const state = this.events.getState();
      const balance = this.events.getLeftRightBalance();

      return {
        tracks,
        hands: this.lastHands,
        throwCount,
        catchCount,
        totalThrows: state.totalThrows,
        totalCatches: state.totalCatches,
        leftThrows: state.leftThrows,
        rightThrows: state.rightThrows,
        leftPct: balance.leftPct,
        rightPct: balance.rightPct,
        meanPeakHeight: this.events.getMeanPeakHeight(),
        isCalibrating: false,
        error: null,
      };
    } catch (err) {
      defaultResult.error = err instanceof Error ? err.message : String(err);
      return defaultResult;
    }
  }

  reset(): void {
    this.events.reset();
  }
}
